import { AnimatedSkeletonList } from "@/components/common/AnimatedSkeletonList/AnimatedSkeletonList"
import { SplitViewQuestionHeader } from "@/components/common/SplitView/SplitViewQuestionHeader"
import { Typography } from "@/components/common/Typography/Typography"
import { ExcelAnswerItem } from "@/components/feature/CollapsibleQuestionnaireList/ExcelAnswerItem/ExcelAnswerItem"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useQuestionListFilters } from "@/pages/questionnaire/useQuestionListFilters"
import { TextAreaStateAction } from "@/types/hooks/useTextAreaStates"
import { Questionnaire } from "@/types/questionnaire/models"

interface TextsCount {
  total: number
  sections: Record<string, number>
}

interface QASectionProps {
  questionnaire: Questionnaire | undefined
  isLoading: boolean
  textsCount: TextsCount
  numTotalQuestions: number
  updateExcelCell: (
    sheetName: string,
    cellAddress: string,
    text: string,
  ) => void
  updateDataToApi: (action: TextAreaStateAction) => void
  /** Registers a per-item callback that forces that item's pending debounced save to run now. */
  registerAnswerFlush?: (responsePairId: string, flush: () => void) => void
  unregisterAnswerFlush?: (responsePairId: string) => void
  disableScroll?: boolean
  disableFirstAnswerSources?: boolean
  disableFirstAnswerTextResize?: boolean
  highlightFirstAnswer?: boolean
}

/**
 * Component that displays the Q&A section with answers list.
 * Handles loading state and displays questionnaire sections.
 */
export const QASection: React.FC<QASectionProps> = ({
  questionnaire,
  isLoading,
  textsCount,
  numTotalQuestions,
  updateDataToApi,
  updateExcelCell,
  registerAnswerFlush,
  unregisterAnswerFlush,
  disableScroll = false,
  disableFirstAnswerSources = false,
  disableFirstAnswerTextResize = false,
  highlightFirstAnswer = false,
}) => {
  const { translate } = useTranslation()
  const { activeFilter, filterCounts, setActiveFilter, visibleSections } =
    useQuestionListFilters(questionnaire)

  if (isLoading) {
    return (
      <>
        <Typography as="h2" variant="titleM" className="pb-L">
          {translate("QUESTIONNAIRE_ANSWERS/PAGE_TITLE")}
        </Typography>
        <AnimatedSkeletonList />
      </>
    )
  }

  if (!questionnaire) {
    return null
  }

  return (
    <div className="flex h-full flex-col">
      <SplitViewQuestionHeader
        countLabelKey="QUESTIONNAIRE_ANSWERS/COUNT_TOTAL_ANSWERS"
        activeFilter={activeFilter}
        filterCounts={filterCounts}
        setActiveFilter={setActiveFilter}
        countValueDisplay={
          <>
            <Typography
              as="span"
              variant="textM"
              className="font-bold text-yellow1"
            >
              {textsCount.total}
            </Typography>
            <Typography
              as="span"
              variant="textM"
              className="font-bold text-white"
            >
              /{numTotalQuestions}
            </Typography>
          </>
        }
      />

      <div
        className={`flex-1 pr-4 pt-XS ${disableScroll ? "overflow-hidden" : "overflow-y-auto"}`}
      >
        <div className="flex flex-col gap-8">
          {visibleSections.map((section, index) => (
            <div
              key={`excel-${section.id}-${index}`}
              className="flex flex-col gap-4"
            >
              {section.visibleResponsePairs.map((pair, pairIndex) => (
                <ExcelAnswerItem
                  key={`${pair.id}-${pairIndex}`}
                  responsePair={pair}
                  sectionId={section.id}
                  updateExcelCell={updateExcelCell}
                  updateDataToApi={updateDataToApi}
                  registerFlush={registerAnswerFlush}
                  unregisterFlush={unregisterAnswerFlush}
                  isHighlighted={
                    highlightFirstAnswer && index === 0 && pairIndex === 0
                  }
                  disableSources={
                    disableFirstAnswerSources && index === 0 && pairIndex === 0
                  }
                  disableTextResize={
                    disableFirstAnswerTextResize &&
                    index === 0 &&
                    pairIndex === 0
                  }
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
